/**
 * Centralized "Ask for more credits" action.
 *
 * Opens the Intercom messenger with a prefilled message so the user can request
 * more Neo credits / a plan upgrade. Shared by the sidebar usage popover and by
 * every Neo refusal surface (out of credits, free trial ended), so the upgrade
 * path lives in exactly one place.
 */

/** CTA label, shared so every surface reads identically. */
export const ASK_FOR_MORE_CREDITS_LABEL = "Ask for more credits";

/** Default prefilled Intercom message (plain out-of-credits case). */
export const ASK_FOR_MORE_CREDITS_MESSAGE = "I'd like to buy more Neo credits.";

/** Prefilled message for the free-trial-ended case (upgrade to paid). */
export const UPGRADE_FROM_TRIAL_MESSAGE =
  "I'd like to upgrade my clinic's Neo plan.";

/**
 * Backend ``error_code`` (HTTP 402) when a post-trial clinic has already spent
 * its daily grace allowance. Kept in sync with the backend
 * (``app/constants/neo_credits.py`` ``NEO_DAILY_GRACE_EXHAUSTED_ERROR_CODE``).
 */
export const DAILY_GRACE_EXHAUSTED_ERROR_CODE = "neo_daily_grace_exhausted";

/**
 * User-facing copy when a clinic has spent its post-trial daily grace for the
 * day ($10 = 100 credits). The CTA (button / modal OK) routes to the upgrade
 * chat, so "Click here" refers to that action.
 */
export const DAILY_GRACE_EXHAUSTED_MESSAGE =
  "Your free trial is over and you've spent your 100 free credits for today. " +
  "Click here to sign up for the paid tier to get more credits.";

/**
 * Open the Intercom messenger with `message` prefilled. No-op when Intercom
 * isn't loaded (e.g. mobile/embedded contexts).
 */
export const openAskForMoreCreditsChat = (
  message: string = ASK_FOR_MORE_CREDITS_MESSAGE
): void => {
  const intercom = (
    window as unknown as {
      Intercom?: (command: string, message?: string) => void;
    }
  ).Intercom;
  intercom?.("showNewMessage", message);
};
